import { KaboodleToaster } from "components/common";
import { useCallback, useState } from "react";
import { useSearchMetadocSummaryMutation } from "services/metadoc-api";

import type { MetadocSummarySearchResult } from "services/metadoc-api";

type UseMetadocSummarySearchParams = {
  metadocId: number;
  onResultActivate?: (result: MetadocSummarySearchResult) => void;
};

/*
 * manages ai summary search execution and activation of the current match.
 */
export const useMetadocSummarySearch = ({
  metadocId,
  onResultActivate,
}: UseMetadocSummarySearchParams) => {
  const [searchResults, setSearchResults] = useState<
    MetadocSummarySearchResult[]
  >([]);
  const [lastSearchQuery, setLastSearchQuery] = useState("");
  const [activeResultIndex, setActiveResultIndex] = useState<number | null>(
    null,
  );
  const [searchSummary, { isLoading: isSearching }] =
    useSearchMetadocSummaryMutation();

  const clearSearch = useCallback(() => {
    setSearchResults([]);
    setLastSearchQuery("");
    setActiveResultIndex(null);
  }, []);

  const handleSearch = useCallback(
    async (query: string) => {
      const trimmedQuery = query.trim();

      if (!trimmedQuery) {
        clearSearch();
        return;
      }

      try {
        const response = await searchSummary({
          metadocId,
          query: trimmedQuery,
        }).unwrap();

        setSearchResults(response.results);
        setLastSearchQuery(trimmedQuery);

        if (response.results.length === 0) {
          setActiveResultIndex(null);
          return;
        }

        setActiveResultIndex(0);
        onResultActivate?.(response.results[0]);
      } catch (error) {
        KaboodleToaster.error(error);
      }
    },
    [clearSearch, metadocId, onResultActivate, searchSummary],
  );

  const activateResultAtIndex = useCallback(
    (resultIndex: number) => {
      const result = searchResults[resultIndex];
      if (!result) {
        return;
      }

      setActiveResultIndex(resultIndex);
      onResultActivate?.(result);
    },
    [onResultActivate, searchResults],
  );

  const handlePreviousResult = useCallback(() => {
    if (searchResults.length === 0) {
      return;
    }

    if (activeResultIndex === null) {
      activateResultAtIndex(0);
      return;
    }

    const previousResultIndex =
      activeResultIndex === 0
        ? searchResults.length - 1
        : activeResultIndex - 1;
    activateResultAtIndex(previousResultIndex);
  }, [activateResultAtIndex, activeResultIndex, searchResults.length]);

  const handleNextResult = useCallback(() => {
    if (searchResults.length === 0) {
      return;
    }

    if (activeResultIndex === null) {
      activateResultAtIndex(0);
      return;
    }

    const nextResultIndex =
      activeResultIndex === searchResults.length - 1
        ? 0
        : activeResultIndex + 1;
    activateResultAtIndex(nextResultIndex);
  }, [activateResultAtIndex, activeResultIndex, searchResults.length]);

  return {
    activeResult:
      activeResultIndex === null
        ? null
        : (searchResults[activeResultIndex] ?? null),
    clearSearch,
    handleSearch,
    handleNextResult,
    handlePreviousResult,
    isSearching,
    lastSearchQuery,
    navigableResultCount: searchResults.length,
    resultCount: searchResults.length,
  };
};
